import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["insetBlockStart", "insetInlineStart"];
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { filterProps } from 'foundations-components/utils/filterProps';
import { PureComponent } from 'react';
import ToolbarActionButtons from './ToolbarActionButtons';
import { TrellisToolbarActionsPopover } from './ToolbarActionsPopover';
import { TrellisToolbarDropdownIconButton } from './ToolbarDropdownIconButton';
import ToolbarIconButton from './ToolbarIconButton';
import ToolbarIconButtonGroup from './ToolbarIconButtonGroup';
import { DEFAULT_RESPONSIVE_SIZE, MEDIUM_RESPONSIVE_SIZE, SMALL_RESPONSIVE_SIZE } from './constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class BaseToolbar extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      tooltipOpen: false
    };
    this.handleDropdownClick = () => {
      const {
        onDropdownOpenChange
      } = this.props;
      this.setState(prevState => {
        const tooltipOpen = !prevState.tooltipOpen;
        setTimeout(() => onDropdownOpenChange(tooltipOpen), 0);
        return {
          tooltipOpen
        };
      });
    };
    this.handleDropdownOpenChange = evt => {
      const {
        onDropdownOpenChange
      } = this.props;
      const tooltipOpen = evt.target.value;
      this.setState({
        tooltipOpen
      });
      onDropdownOpenChange(tooltipOpen);
    };
    this.executeAction = ({
      callback
    }) => {
      const {
        onDropdownOpenChange
      } = this.props;
      const {
        tooltipOpen
      } = this.state;
      if (tooltipOpen) {
        this.setState({
          tooltipOpen: false
        });
        onDropdownOpenChange(false);
      }
      callback();
    };
    this.renderDropdownButton = buttonProps => {
      const {
        active: isToolbarActive,
        actions,
        lastButtonRounding,
        onlyLeftAndRightButtonBorders,
        iconUse,
        CustomDropdownButton,
        label,
        responsiveSize,
        isTrellisTheme,
        variant
      } = this.props;
      if (CustomDropdownButton) {
        return /*#__PURE__*/_jsx(CustomDropdownButton, Object.assign({}, buttonProps, {
          label: label,
          responsiveSize: responsiveSize,
          isToolbarActive: isToolbarActive,
          iconUse: iconUse
        }));
      }
      const {
        tooltipOpen
      } = this.state;
      const buttonActions = actions.filter(a => !a.onlyInDropdown);
      const hasInlineActions = !!buttonActions && buttonActions.length > 0;
      const hasAnyBorder = !onlyLeftAndRightButtonBorders || hasInlineActions;
      return /*#__PURE__*/_jsx(TrellisToolbarDropdownIconButton, Object.assign({}, buttonProps, {
        active: tooltipOpen,
        iconUse: iconUse,
        isToolbarActive: isToolbarActive,
        hasAnyBorder: hasAnyBorder,
        onlyLeftAndRightBorders: onlyLeftAndRightButtonBorders,
        cornerToRound: lastButtonRounding,
        isTrellisTheme: isTrellisTheme,
        variant: variant
      }));
    };
  }
  renderActionsDropdown({
    dropdownActions
  }) {
    const {
      ActionsDropdown,
      dropdownPlacement
    } = this.props;
    const {
      tooltipOpen
    } = this.state;
    if (dropdownActions.length === 0) {
      return null;
    }
    return /*#__PURE__*/_jsx(ActionsDropdown, {
      open: tooltipOpen,
      onOpenChange: this.handleDropdownOpenChange,
      executeAction: this.executeAction,
      placement: dropdownPlacement,
      actions: dropdownActions,
      Button: this.renderDropdownButton
    });
  }
  renderFirstHandleButton(options) {
    const {
      isDefault,
      isSmall
    } = options;
    const {
      active,
      firstButtonRounding,
      handleTooltip,
      handleTooltipPlacement,
      label,
      icon,
      iconUse,
      onHandleMouseDown,
      onHandleClick,
      rotateHandleIcon = '0deg',
      handleButtonRef,
      handleButtonStyles,
      variant = 'handle',
      renderIcon,
      isTrellisTheme
    } = this.props;
    const tooltipToUse = handleTooltip || label;
    return Boolean(icon || renderIcon) && /*#__PURE__*/_jsx(ToolbarIconButton, Object.assign({
      borderInlineStartWidth: "100",
      icon: isSmall ? undefined : icon,
      renderIcon: renderIcon,
      isToolbarActive: active,
      rotate: rotateHandleIcon,
      cornerToRound: firstButtonRounding,
      use: iconUse,
      onClick: onHandleClick,
      onMouseDown: onHandleMouseDown,
      title: isDefault && label ? label : undefined,
      tooltip: tooltipToUse,
      placement: handleTooltipPlacement,
      ref: handleButtonRef,
      variant: variant === 'aiEditor' ? 'aiEditorHandle' : variant,
      isTrellisTheme: isTrellisTheme
    }, handleButtonStyles));
  }
  renderFirstHandleButtonWithTooltipIfNeeded(options) {
    const {
      isDefault
    } = options;
    const {
      label,
      handleTooltip,
      handleTooltipPlacement
    } = this.props;
    const tooltipToUse = handleTooltip || label;
    const showingALabel = label && isDefault;
    const innerElements = this.renderFirstHandleButton(options);
    if (!innerElements) {
      return null;
    }
    if (!tooltipToUse || showingALabel) {
      return innerElements;
    }
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: handleTooltipPlacement,
      title: tooltipToUse,
      children: innerElements
    });
  }
  render() {
    const {
      active,
      actions,
      responsiveSize,
      forwardedRef,
      lastButtonRounding,
      isTrellisTheme,
      variant
    } = this.props;
    const isSmall = responsiveSize === SMALL_RESPONSIVE_SIZE;
    const isMedium = responsiveSize === MEDIUM_RESPONSIVE_SIZE;
    const isDefault = responsiveSize === DEFAULT_RESPONSIVE_SIZE;
    const {
      iconUse,
      transform,
      transformOrigin,
      rotateActionIcons
    } = this.props;
    const {
      tokenProps,
      domProps
    } = filterProps(this.props);
    const {
        insetBlockStart,
        insetInlineStart
      } = tokenProps,
      restTokenProps = _objectWithoutPropertiesLoose(tokenProps, _excluded);
    // Hide all of action buttons in the medium and small size
    const dropdownActions = isDefault ? actions.filter(a => a.onlyInDropdown) : actions;
    return /*#__PURE__*/_jsxs(ToolbarIconButtonGroup, Object.assign({
      ref: forwardedRef,
      transform: transform,
      transformOrigin: transformOrigin,
      style: {
        insetBlockStart,
        insetInlineStart
      }
    }, restTokenProps, domProps, {
      children: [this.renderFirstHandleButtonWithTooltipIfNeeded({
        isSmall,
        isMedium,
        isDefault
      }), isDefault && /*#__PURE__*/_jsx(ToolbarActionButtons, {
        buttonActions: actions.filter(a => !a.onlyInDropdown),
        hasDropdown: dropdownActions.length > 0,
        active: active,
        lastButtonRounding: lastButtonRounding,
        iconUse: iconUse,
        rotateActionIcons: rotateActionIcons,
        isTrellisTheme: isTrellisTheme,
        variant: variant
      }), this.renderActionsDropdown({
        dropdownActions
      })]
    }));
  }
}
BaseToolbar.defaultProps = {
  responsiveSize: DEFAULT_RESPONSIVE_SIZE,
  dropdownPlacement: 'bottom',
  onlyLeftAndRightButtonBorders: false,
  style: {},
  onDropdownOpenChange: () => {},
  ActionsDropdown: TrellisToolbarActionsPopover,
  draggable: false
};
export const TrellisBaseToolbar = BaseToolbar;
export default TrellisBaseToolbar;